import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import useAuth from "../hooks/useAuth";
import { PROXY } from "../lib/utils";

const pageSize = 8;

export default function LessonDiscussion({ courseId, lessonId, focusCommentId, assignedTeacherId }) {
  const { t } = useTranslation();
  const { user, accessToken, isAuth } = useAuth();
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ comments: [], replyCounts: {}, pagination: {} });
  const [state, setState] = useState("loading");
  const [draft, setDraft] = useState("");
  const [replyDrafts, setReplyDrafts] = useState({});
  const [threads, setThreads] = useState({});
  const [busy, setBusy] = useState(false);
  const [revision, setRevision] = useState(0);
  const endpoint = `${PROXY}/courses/${courseId}/lessons/${lessonId}/comments`;
  const isStudent = isAuth && user?.role === "student";
  const isTeacher = isAuth && user?.role === "teacher" && user?.studioAccess && String(user?._id || user?.id) === String(assignedTeacherId);

  useEffect(() => { setPage(1); setThreads({}); setDraft(""); }, [courseId, lessonId]);
  useEffect(() => {
    if (!courseId || !lessonId) return;
    const controller = new AbortController();
    setState("loading");
    fetch(`${endpoint}?page=${page}&limit=${pageSize}${focusCommentId ? `&focus=${encodeURIComponent(focusCommentId)}` : ""}`, { signal: controller.signal })
      .then((response) => { if (!response.ok) throw new Error(); return response.json(); })
      .then((result) => { if (!controller.signal.aborted) { setData(result); setState("ready"); } })
      .catch(() => { if (!controller.signal.aborted) setState("error"); });
    return () => controller.abort();
  }, [endpoint, page, revision, courseId, lessonId, focusCommentId]);

  const loadReplies = useCallback(async (parentId, nextPage = 1) => {
    try {
      const response = await fetch(`${endpoint}?parent=${parentId}&page=${nextPage}&limit=${pageSize}`);
      if (!response.ok) throw new Error();
      const result = await response.json();
      setThreads((previous) => ({ ...previous, [parentId]: result }));
    } catch { toast.error(t("lessonDiscussion.loadFailed")); }
  }, [endpoint, t]);

  const send = async (path, content, onSuccess) => {
    if (content.trim().length < 2 || content.trim().length > 1000) {
      toast.error(t("lessonDiscussion.inputLimit")); return;
    }
    setBusy(true);
    try {
      const response = await fetch(`${endpoint}${path}`, { method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${accessToken}` },
        body: JSON.stringify({ content: content.trim() }) });
      if (!response.ok) throw new Error();
      onSuccess();
      setRevision((value) => value + 1);
      toast.success(t("lessonDiscussion.posted"));
    } catch { toast.error(t("lessonDiscussion.postFailed")); }
    finally { setBusy(false); }
  };

  const report = async (commentId) => {
    try {
      const response = await fetch(`${endpoint}/${commentId}/report`, { method: "POST",
        headers: { Authorization: `Bearer ${accessToken}` } });
      if (!response.ok) throw new Error();
      toast.success(t("lessonDiscussion.reported"));
    } catch { toast.error(t("lessonDiscussion.reportFailed")); }
  };

  const renderComment = (comment, nested = false) => <article key={comment._id} id={`lesson-comment-${comment._id}`}
    className={`rounded-xl border p-3 ${String(focusCommentId) === String(comment._id) ? "border-indigo-500 bg-indigo-50" : "border-gray-200 bg-white"}`}>
    <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-gray-500">
      <span className="font-semibold text-gray-800">{comment.author?.fullName || t("lessonDiscussion.learner")}{comment.isTeacherReply && <span className="ms-2 rounded bg-indigo-100 px-2 py-0.5 text-indigo-800">{t("lessonDiscussion.teacher")}</span>}</span>
      <time dateTime={comment.createdAt}>{new Date(comment.createdAt).toLocaleDateString()}</time>
    </div>
    <p className="mt-2 whitespace-pre-wrap break-words text-sm text-gray-800">{comment.content}</p>
    {isAuth && String(user?._id || user?.id) !== String(comment.author?._id) && <button type="button" onClick={() => report(comment._id)} className="mt-2 rounded text-xs text-gray-600 underline focus:ring-2 focus:ring-indigo-500">{t("lessonDiscussion.report")}</button>}
    {!nested && <div className="mt-3 space-y-2 border-s border-gray-200 ps-3">
      {threads[comment._id]?.comments?.map((reply) => renderComment(reply, true))}
      {(data.replyCounts?.[comment._id] > 0 || threads[comment._id]?.pagination?.totalPages > 1) && <button type="button" onClick={() => loadReplies(comment._id, threads[comment._id]?.pagination?.page && threads[comment._id].pagination.page < threads[comment._id].pagination.totalPages ? threads[comment._id].pagination.page + 1 : 1)} className="rounded px-2 py-1 text-xs font-medium text-indigo-700 hover:bg-indigo-50 focus:ring-2 focus:ring-indigo-500">{t("lessonDiscussion.showReplies")} ({data.replyCounts?.[comment._id] || 0})</button>}
      {isTeacher && <form onSubmit={(event) => { event.preventDefault(); send(`/${comment._id}/replies`, replyDrafts[comment._id] || "", () => { setReplyDrafts((previous) => ({ ...previous, [comment._id]: "" })); loadReplies(comment._id); }); }} className="space-y-2">
        <label htmlFor={`reply-${comment._id}`} className="block text-xs font-medium">{t("lessonDiscussion.reply")}</label>
        <textarea id={`reply-${comment._id}`} maxLength={1000} value={replyDrafts[comment._id] || ""} onChange={(event) => setReplyDrafts((previous) => ({ ...previous, [comment._id]: event.target.value }))} className="w-full rounded-lg border p-2 text-sm focus:ring-2 focus:ring-indigo-500" rows={2} />
        <button type="submit" disabled={busy} className="rounded-lg bg-indigo-700 px-3 py-2 text-xs font-semibold text-white disabled:opacity-50">{t("lessonDiscussion.sendReply")}</button>
      </form>}
    </div>}
  </article>;

  return <section className="mt-6 max-w-3xl rounded-2xl border border-gray-200 bg-gray-50 p-3 sm:p-5" aria-label={t("lessonDiscussion.title")}>
    <h2 className="mb-3 text-lg font-bold text-gray-900">{t("lessonDiscussion.title")}</h2>
    {isStudent ? <form onSubmit={(event) => { event.preventDefault(); send("", draft, () => setDraft("")); }} className="mb-4 space-y-2">
      <label htmlFor={`lesson-draft-${lessonId}`} className="block text-sm font-medium">{t("lessonDiscussion.ask")}</label>
      <textarea id={`lesson-draft-${lessonId}`} maxLength={1000} value={draft} onChange={(event) => setDraft(event.target.value)} rows={3} className="w-full rounded-lg border p-2 focus:ring-2 focus:ring-indigo-500" />
      <p className="text-xs text-gray-500">{draft.length}/1000</p>
      <button type="submit" disabled={busy} className="rounded-lg bg-indigo-700 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50">{t("lessonDiscussion.post")}</button>
    </form> : !isAuth ? <p className="mb-4 text-sm text-gray-700">{t("lessonDiscussion.loginRequired")} <Link to="/login" className="font-semibold text-indigo-700 underline">{t("lessonDiscussion.login")}</Link></p> : null}
    {state === "loading" && <p className="text-sm text-gray-500">{t("lessonDiscussion.loading")}</p>}
    {state === "error" && <p role="alert" className="text-sm text-red-700">{t("lessonDiscussion.loadFailed")}</p>}
    {state === "ready" && <div className="space-y-3">{data.comments.length ? data.comments.map((comment) => renderComment(comment)) : <p className="text-sm text-gray-500">{t("lessonDiscussion.empty")}</p>}</div>}
    {data.pagination?.totalPages > 1 && <nav className="mt-4 flex items-center gap-3" aria-label={t("lessonDiscussion.pages")}><button type="button" disabled={page <= 1} onClick={() => setPage((value) => value - 1)} className="rounded border px-3 py-1 disabled:opacity-50">{t("lessonDiscussion.previous")}</button><span>{page} / {data.pagination.totalPages}</span><button type="button" disabled={page >= data.pagination.totalPages} onClick={() => setPage((value) => value + 1)} className="rounded border px-3 py-1 disabled:opacity-50">{t("lessonDiscussion.next")}</button></nav>}
  </section>;
}
